
if (!customElements.get('mercado-pago-pdp')) {
    class MercadoPagoPdp extends HTMLElement {
        constructor() {
            super();
            this.installments = parseInt(this.dataset.installments || '3', 10);
            this.locale = this.dataset.locale || 'es-CO';
            this.currency = this.dataset.currency || 'COP';
            this.currentPrice = parseInt(this.dataset.currentPrice || '0', 10);
            this.variantId = this.dataset.variantId || '';
            this.minimumPrice = parseInt(this.dataset.minimumPrice || '0', 10);
            this.moneyFactor = parseInt(this.dataset.moneyFactor || '100', 10);
            this.productId = this.dataset.productId || '';
            this.discountPriceContainer = null;
            this.discountPriceObserver = null;
            this.updatePrice = this.updatePrice.bind(this);
            this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
            this.handleDiscountPriceMutations = this.handleDiscountPriceMutations.bind(this);
        }

        connectedCallback() {
            this.priceEl = this.querySelector('.mp-price-amount');
            this.section = this.closest('.shopify-section, dialog');
            this.section?.addEventListener('variant:update', this.handleVariantUpdate);
            document.addEventListener('variant-price-fetched', this.updatePrice);
            this.syncWidget();
            this.observeDiscountPriceContainer();
            this.syncFromDiscountPriceContainer();
        }

        disconnectedCallback() {
            this.section?.removeEventListener('variant:update', this.handleVariantUpdate);
            document.removeEventListener('variant-price-fetched', this.updatePrice);
            this.disconnectDiscountPriceObserver();
        }

        getDiscountPriceContainer() {
            return this.section?.querySelector('[data-discount-price]') || null;
        }

        disconnectDiscountPriceObserver() {
            if (this.discountPriceObserver) {
                this.discountPriceObserver.disconnect();
                this.discountPriceObserver = null;
            }

            this.discountPriceContainer = null;
        }

        observeDiscountPriceContainer() {
            const nextContainer = this.getDiscountPriceContainer();

            if (nextContainer === this.discountPriceContainer) {
                return;
            }

            this.disconnectDiscountPriceObserver();
            this.discountPriceContainer = nextContainer;

            if (!(this.discountPriceContainer instanceof HTMLElement) || typeof MutationObserver === 'undefined') {
                return;
            }

            this.discountPriceObserver = new MutationObserver(this.handleDiscountPriceMutations);
            this.discountPriceObserver.observe(this.discountPriceContainer, {
                attributes: true,
                attributeFilter: ['data-current-price-cents', 'data-variant-id'],
            });
        }

        handleDiscountPriceMutations() {
            this.syncFromDiscountPriceContainer();
        }

        syncFromDiscountPriceContainer() {
            const priceContainer = this.getDiscountPriceContainer();
            if (!(priceContainer instanceof HTMLElement)) return;

            const containerVariantId = priceContainer.dataset.variantId || '';
            if (this.variantId && containerVariantId && containerVariantId !== this.variantId) {
                return;
            }

            const currentPriceCents = parseInt(priceContainer.dataset.currentPriceCents || '0', 10);
            if (Number.isNaN(currentPriceCents) || currentPriceCents <= 0) return;

            this.currentPrice = currentPriceCents;
            this.dataset.currentPrice = String(currentPriceCents);
            this.syncWidget();
        }

        syncWidget() {
            const shouldShow = this.currentPrice >= this.minimumPrice;

            this.toggleAttribute('hidden', !shouldShow);
            this.setAttribute('aria-hidden', String(!shouldShow));

            if (!shouldShow || !this.priceEl) return;

            const installmentPrice = this.currentPrice / this.moneyFactor / this.installments;
            if (isNaN(installmentPrice)) return;

            const formatted = new Intl.NumberFormat(this.locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(installmentPrice);

            this.priceEl.textContent = `$${formatted}`;
        }

        handleVariantUpdate(event) {
            if (event.detail?.data?.newProduct) {
                this.productId = event.detail.data.newProduct.id;
            } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.productId) {
                return;
            }

            const updatedWidget = event.detail?.data?.html?.querySelector('mercado-pago-pdp');
            if (!(updatedWidget instanceof HTMLElement)) return;

            this.dataset.installments = updatedWidget.dataset.installments || this.dataset.installments;
            this.dataset.locale = updatedWidget.dataset.locale || this.dataset.locale;
            this.dataset.currency = updatedWidget.dataset.currency || this.dataset.currency;
            this.dataset.currentPrice = updatedWidget.dataset.currentPrice || this.dataset.currentPrice;
            this.dataset.variantId = updatedWidget.dataset.variantId || this.dataset.variantId;
            this.dataset.minimumPrice = updatedWidget.dataset.minimumPrice || this.dataset.minimumPrice;
            this.dataset.moneyFactor = updatedWidget.dataset.moneyFactor || this.dataset.moneyFactor;
            this.dataset.productId = updatedWidget.dataset.productId || this.dataset.productId;
            this.innerHTML = updatedWidget.innerHTML;

            this.installments = parseInt(this.dataset.installments || '3', 10);
            this.locale = this.dataset.locale || 'es-CO';
            this.currency = this.dataset.currency || 'COP';
            this.currentPrice = parseInt(this.dataset.currentPrice || '0', 10);
            this.variantId = this.dataset.variantId || '';
            this.minimumPrice = parseInt(this.dataset.minimumPrice || '0', 10);
            this.moneyFactor = parseInt(this.dataset.moneyFactor || '100', 10);
            this.productId = this.dataset.productId || '';
            this.priceEl = this.querySelector('.mp-price-amount');

            this.syncWidget();
            this.observeDiscountPriceContainer();
            this.syncFromDiscountPriceContainer();
        }

        /**
         * @param {CustomEvent} event
         */
        updatePrice(event) {
            if (!this.priceEl) {
                this.priceEl = this.querySelector('.mp-price-amount');
                if (!this.priceEl) return;
            }

            if (!(event.target instanceof Element)) return;
            const eventSection = event.target.closest('.shopify-section, [data-section-type]');
            const mySection = this.closest('.shopify-section, [data-section-type]');

            if (eventSection && mySection && eventSection !== mySection) {
                return;
            }

            if (event.detail && event.detail.displayPrice) {
                const newPrice = parseFloat(event.detail.displayPrice);
                const shouldShow = newPrice >= this.minimumPrice / this.moneyFactor;

                this.toggleAttribute('hidden', !shouldShow);
                this.setAttribute('aria-hidden', String(!shouldShow));

                if (!shouldShow) {
                    return;
                }

                const installmentPrice = newPrice / this.installments;

                if (!isNaN(installmentPrice)) {
                    const formatted = new Intl.NumberFormat(this.locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(installmentPrice);

                    this.priceEl.textContent = `$${formatted}`;
                }
            }
        }
    }
    customElements.define('mercado-pago-pdp', MercadoPagoPdp);
}
